import axios from "axios";

/**
 * The ONE axios instance for the whole app.
 *
 * - baseURL from env (REACT_APP_API_URL) with a localhost default.
 * - withCredentials: true so the httpOnly `token` cookie set on login is
 *   sent on every request. The SPA never reads/stores the JWT itself.
 * - Response interceptor: on 401 (session expired / not authenticated) send
 *   the user back to /login — except for the auth probe endpoints, whose
 *   callers handle 401/403 themselves (initial /auth/me, login, register).
 */
const client = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:4000/api",
  withCredentials: true,
});

const AUTH_PROBE = ["/auth/me", "/auth/login", "/auth/register", "/auth/logout"];

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const isAuthProbe = AUTH_PROBE.some((p) => url.includes(p));

    if (status === 401 && !isAuthProbe) {
      const path = window.location.pathname;
      if (!path.startsWith("/login")) {
        window.location.assign("/login");
      }
    }
    return Promise.reject(error);
  }
);

// The API caps `limit` at 100 server-side (see backend/src/utils/pagination.js),
// so a single `limit: 1000` request silently truncates any collection with
// more than 100 rows — a page assuming it got "everything" for a local
// id -> record lookup map then just can't resolve refs past row 100 (e.g. an
// org's 500 STBs would only ever populate the first 100). Page through the
// full list instead. `total` (from response `meta`) bounds the loop;
// MAX_BATCHES is a belt-and-suspenders guard against a runaway loop.
const FETCH_ALL_BATCH_SIZE = 100;
const FETCH_ALL_MAX_BATCHES = 200; // 200 * 100 = 20,000 rows

/** Fetch every row of a paginated `GET path` endpoint, looping page-by-page. */
export async function fetchAllPages(path, extraParams = {}) {
  const all = [];
  let page = 1;
  let total = Infinity;
  while (all.length < total && page <= FETCH_ALL_MAX_BATCHES) {
    const res = await client.get(path, {
      params: { ...extraParams, page, limit: FETCH_ALL_BATCH_SIZE },
    });
    const rows = res.data?.data || [];
    total = res.data?.meta?.total ?? rows.length;
    all.push(...rows);
    if (rows.length === 0) break; // safety: avoid looping on an unexpected empty page
    page += 1;
  }
  return all;
}

export default client;
